export interface ActionableGoogleMimeBehavior {
  action: 'direct' | 'export';
  outMime: string;
  extension: string;
}

export type GoogleMimeTypeBehavior = {action: 'not_allowed'} | ActionableGoogleMimeBehavior;

export enum GoogleDriveIssueType {
  GoogleDriveUnreachable,
  UnableToExportFile,
  UnableToDirectDownloadFile,
  InvalidFilePicked,
}

export interface GoogleDriveIssue {
  issueType: GoogleDriveIssueType;
  userDescription: string;
  devDescription: string;
  googleErrorMessage?: string;
}

async function getGoogleErrorMessageFromResponse(response: Response): Promise<string> {
  const responseJson = await response.json();
  return `${JSON.stringify(responseJson?.error?.message ?? 'Unknown error')}`;
}

export async function createUnableToDirectDownloadFileIssue(
  inputFileInfo: InputFileInfo,
  response: Response
): Promise<GoogleDriveIssue> {
  return {
    issueType: GoogleDriveIssueType.UnableToDirectDownloadFile,
    userDescription: `Error downloading file ${inputFileInfo.inputFileName} from Google Drive`,
    devDescription: `HTTP ${response.status}: ${response.statusText} error in downloading file "${inputFileInfo.inputFileName}"`,
    googleErrorMessage: await getGoogleErrorMessageFromResponse(response),
  };
}

export async function createUnableToExportFileIssue(
  inputFileInfo: InputFileInfo,
  outputFileInfo: ExportOutputFileInfo,
  response: Response
): Promise<GoogleDriveIssue> {
  return {
    issueType: GoogleDriveIssueType.UnableToExportFile,
    userDescription: `Error exporting file ${inputFileInfo.inputFileName} to file type ${outputFileInfo.outputFileExtension} in Google Drive`,
    devDescription: `HTTP ${response.status}: ${response.statusText} error in exporting file "${inputFileInfo.inputFileName}" with mimeType "${inputFileInfo.inputFileMimeType}" to mimeType "${outputFileInfo.outputFileMimeType}"`,
    googleErrorMessage: await getGoogleErrorMessageFromResponse(response),
  };
}

export const GoogleDriveUnreachableIssue: GoogleDriveIssue = {
  issueType: GoogleDriveIssueType.GoogleDriveUnreachable,
  userDescription: 'Cannot reach Google Drive',
  devDescription: 'Cannot reach Google Drive',
};

export interface InputFileInfo {
  inputFileId: string;
  inputFileName: string;
  inputFileMimeType: string;
}

export interface DirectDownloadOutputFileInfo {
  outputFileName: string;
  outputFileMimeType: string;
}

export interface ExportOutputFileInfo extends DirectDownloadOutputFileInfo {
  outputFileExtension: string;
}

export type DownloadSingleGoogleDriveFileInstructions =
  | {
      action: 'direct';
      inputFileInfo: InputFileInfo;
      outputFileInfo: DirectDownloadOutputFileInfo;
    }
  | {
      action: 'export';
      inputFileInfo: InputFileInfo;
      outputFileInfo: ExportOutputFileInfo;
    };

export type DownloadResult =
  | {fileId: string; file: File; error: null}
  | {fileId: string; error: GoogleDriveIssue; file: null};

export function createInvalidFilePickedDownloadResult(
  fileId: string,
  userDescription: string,
  devDescription: string
): DownloadResult {
  return {
    fileId,
    file: null,
    error: {
      issueType: GoogleDriveIssueType.InvalidFilePicked,
      userDescription,
      devDescription,
    },
  };
}
